import { useEffect, useRef, useState } from "react";
import { useHub } from "../../../../core/Global";
import { toastSuccess, toastError } from "../../../../components/base/Toast";
import Icon from "../../../../components/base/Icon";
import Indicator from "../../../../components/base/Indicator";
import Div from "../../../../components/base/Div";
import AddonCatalog, { AddonPurchase, AddonState } from "../models/AddonCatalog";
import AddonLibraryDrawer from "./AddonLibraryDrawer";
import { DrawerRef } from "../../../../components/drawers/Drawer";
import PlanAddonDrawer from "./PlanAddonDrawer";
import ExtraUsersDrawer from "./ExtraUsersDrawer";
import AddonDetailsDrawer from "./AddonDetailsDrawer";
import CurrentPlan from "../models/CurrentPlan";
import "./AddonsCard.css";


// The add-ons the account has, as a wall of tiles with an empty slot at the end.
//
// Sits under the subscription on the same page: what you pay every month, and then what you have
// bolted on to it. Its own card, because they are two different questions.
//
// Nothing for a customer still on the old subscription. Their add-ons are separate subscriptions
// with renewals and cancellations of their own, and the card that manages those is already on
// this page -- two would be one too many, and only one of them can cancel.
export default function AddonsCard(props: {
    // Told when a purchase has changed the account, so the plan card above can re-read it.
    onChanged?: () => void,
    // Changes when the hub says the subscription changed; the wall reads itself again quietly.
    refreshKey?: number
}) {

    const hub = useHub()

    const [items, itemsSet] = useState<AddonCatalog[]>([])
    const [fetching, fetchingSet] = useState<boolean>(true)
    const [legacy, legacySet] = useState<boolean>(false)

    const [libraryOpen, libraryOpenSet] = useState<boolean>(false)
    const libraryRef = useRef<DrawerRef>(null)
    const [buying, buyingSet] = useState<AddonCatalog>()

    // Which tile's details are open: the add-on, and for extra users the one purchase. Held as
    // keys rather than a copy, so a refresh after cancelling shows up in the open drawer.
    const [detail, detailSet] = useState<{ key?: string, purchaseId?: string }>()
    const detailRef = useRef<DrawerRef>(null)

    // A silent refresh leaves the wall on screen while it runs, for changes made from a drawer
    // that is still open over it.
    const fetch = (silent: boolean = false) => {
        if (!silent) fetchingSet(true)
        hub.Get("/accounts/profile/plan/addons",
            res => { itemsSet(res.data ?? []); fetchingSet(false) },
            () => { fetchingSet(false); toastError("Could not load your add-ons.") })
    }

    useEffect(() => {
        hub.Get("/accounts/profile/plan", res => {
            const plan: CurrentPlan | undefined = res.data
            if (plan?.isLegacy) { legacySet(true); fetchingSet(false); return }
            fetch()
        }, () => fetchingSet(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (props.refreshKey && !legacy) fetch(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.refreshKey])

    // Coming back from Checkout after paying for one with a new card.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        if (params.get("kind") !== "addon") return

        const payment = params.get("payment")
        if (!payment) return

        if (payment === "success") toastSuccess("Your add-on is active.")
        else toastError(params.get("message") || "The add-on payment failed.")

        params.delete("payment"); params.delete("message"); params.delete("kind")
        const clean = window.location.pathname + (params.toString() ? "?" + params.toString() : "")
        window.history.replaceState({}, "", clean)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // What is on the wall is what the customer actually has.
    const owned = items.filter(i => i.state === AddonState.Included || i.state === AddonState.Active)

    // An add-on bought by the number is drawn once per purchase: each was bought on its own day
    // and renews on its own date, and one tile with the soonest date made a second purchase look
    // as though it had been added to the first.
    const tiles = (item: AddonCatalog, key: number) =>
        item.byQuantity && (item.purchases?.length ?? 0) > 0
            ? item.purchases!.map((p, k) => tile(item, key + "-" + k, p))
            : [tile(item, key)]

    const tile = (item: AddonCatalog, key: number | string, purchase?: AddonPurchase) => (
        <div key={key}
            className={"addon-tile is-clickable" + (item.state === AddonState.Included ? " is-included" : " is-active")}
            onClick={() => detailSet({ key: item.key, purchaseId: purchase?.id })}>

            <div className="addon-tile-head">
                <div className="addon-tile-icon">
                    {
                        item.image ?
                            <img src={item.image} alt={item.name} />
                            :
                            <Icon icon={item.icon} type={item.iconType} size={20} />
                    }
                </div>
                <div className="addon-tile-name">{purchase?.name ?? item.name}</div>
            </div>

            <div className="addon-tile-tagline">{item.tagline}</div>

            <div className="addon-tile-foot">
                {
                    (purchase ? purchase.isCanceled : item.isCanceled) && item.state !== AddonState.Included ?
                        <span className="badge fw-bold px-3 py-2 badge-light-danger">
                            <Icon icon="clock" type="solid" size={10} className="me-1" />
                            Cancelled
                        </span>
                        :
                        <span className={"badge fw-bold px-3 py-2 "
                            + (item.state === AddonState.Included ? "badge-light-primary" : "badge-light-success")}>
                            <Icon icon="circle-check" type="solid" size={10} className="me-1" />
                            {item.state === AddonState.Included ? "In your plan" : "Active"}
                        </span>
                }
                {
                    item.state === AddonState.Active &&
                    <span className="addon-tile-price">{purchase?.priceText ?? item.priceText}</span>
                }
                {
                    (purchase?.note ?? item.note) &&
                    <div className="addon-tile-note">{purchase?.note ?? item.note}</div>
                }
            </div>

        </div>
    )

    const detailItem = detail ? items.find(i => i.key === detail.key) : undefined
    const detailPurchase = detail?.purchaseId
        ? detailItem?.purchases?.find(p => p.id === detail.purchaseId)
        : undefined

    if (legacy) return <></>

    return (
        <>
            <div className="card mb-5">
                <div className="card-header border-0">
                    <div className="card-title m-0">
                        <h3 className="fw-bolder m-0">Addons</h3>
                    </div>
                </div>
                <div className="card-body border-top p-9">

                    <Div visible={fetching} className="my-20">
                        <Indicator />
                    </Div>

                    {
                        !fetching &&
                        <div className="addon-wall">

                            {owned.map((item, key) => tiles(item, key))}

                            {/* The empty slot, always last. It says the wall is meant to be
                                filled -- a button above the grid would leave it looking done. */}
                            <div className="addon-slot" onClick={() => libraryOpenSet(true)}>
                                <div className="addon-slot-plus">
                                    <Icon icon="plus" type="solid" size={20} />
                                </div>
                                <div className="addon-slot-text">Add an addon</div>
                                <div className="addon-slot-hint">
                                    {
                                        owned.length === 0
                                            ? "Browse what you can add to your account"
                                            : "Browse the library"
                                    }
                                </div>
                            </div>

                        </div>
                    }

                </div>
            </div>

            {
                libraryOpen &&
                <AddonLibraryDrawer
                    drawerRef={libraryRef}
                    items={items}
                    loading={fetching}
                    onClose={() => libraryOpenSet(false)}
                    onAdd={item => buyingSet(item)} />
            }

            {
                /* Neither drawer is unmounted to close it. DrawerForm hides the purchase drawer
                   itself once the charge comes back successful, and the library is asked to hide
                   through its ref -- both then unmount from their own onClose, after the backdrop
                   has gone. Tearing them down here would leave that backdrop over the page. */
                buying && !buying.byQuantity &&
                <PlanAddonDrawer
                    capability={buying}
                    onClose={() => buyingSet(undefined)}
                    onPaid={() => { fetch(); props.onChanged?.(); libraryRef.current?.close(); }} />
            }

            {
                /* Closed through its ref, then unmounted from its own onClose, so the backdrop
                   goes with it. The item is looked up fresh on every render, so what an action
                   in it changes shows up without closing it. */
                detailItem &&
                <AddonDetailsDrawer
                    drawerRef={detailRef}
                    item={detailItem}
                    purchase={detailPurchase}
                    onClose={() => detailSet(undefined)}
                    onChanged={() => { fetch(true); props.onChanged?.(); }} />
            }

            {
                /* Extra users are bought by the number, and can be bought again while some are
                   running -- so they have a drawer of their own. */
                buying && buying.byQuantity &&
                <ExtraUsersDrawer
                    capability={buying}
                    onClose={() => buyingSet(undefined)}
                    onPaid={() => { fetch(); props.onChanged?.(); libraryRef.current?.close(); }} />
            }
        </>
    )
}
